import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../App';
import { useDateFormat } from '../../contexts/DateFormatContext';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sparkles, Settings, Loader2, Target, TrendingUp, Lock, Crown, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PersonalizedPredictions = () => {
  const { user } = useContext(AuthContext);
  const { formatDate } = useDateFormat();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [options, setOptions] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [showForm, setShowForm] = useState(false);
  
  // Format time from ISO string
  const formatMatchTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  // Form state
  const [criteria, setCriteria] = useState({
    total_odds_range: '2-3',
    min_odds: '1.30',
    markets: [],
    leagues: []
  });

  const isPremium = user?.subscription_plan && user.subscription_plan !== 'free';

  useEffect(() => {
    if (isPremium) {
      fetchOptions();
    } else {
      setOptionsLoading(false);
    }
  }, [isPremium]);

  const fetchOptions = async () => {
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.get(`${API}/predictions-ai/available-markets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOptions(response.data);
    } catch (error) {
      console.error('Error fetching options:', error);
      toast.error('Failed to load prediction options');
    } finally {
      setOptionsLoading(false);
    }
  };

  const handleMarketToggle = (marketId) => {
    setCriteria(prev => ({
      ...prev,
      markets: prev.markets.includes(marketId)
        ? prev.markets.filter(m => m !== marketId)
        : [...prev.markets, marketId]
    }));
  };

  const handleLeagueToggle = (leagueId) => {
    setCriteria(prev => ({
      ...prev,
      leagues: prev.leagues.includes(leagueId)
        ? prev.leagues.filter(l => l !== leagueId)
        : [...prev.leagues, leagueId]
    }));
  };

  const generatePrediction = async () => {
    setLoading(true);
    setPrediction(null);
    
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.post(`${API}/predictions-ai/personalized`, criteria, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setPrediction(response.data.prediction);
        toast.success('Personalized prediction generated!');
      } else {
        toast.error(response.data.message || 'No matches found matching your criteria');
      }
    } catch (error) {
      console.error('Error generating prediction:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate prediction');
    } finally {
      setLoading(false);
    }
  };

  // Non-premium users don't see this component (upgrade card shown in statistics section)
  if (!isPremium) {
    return null;
  }

  if (optionsLoading) {
    return (
      <Card className="glass-dark border-white/10 mb-6">
        <CardContent className="py-8">
          <div className="flex items-center justify-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
            <span className="text-gray-400">Loading prediction options...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-dark border-white/10 mb-6 overflow-hidden relative">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 via-blue-500 to-purple-500" />
      
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-400" />
            Personalized AI Predictions
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowForm(!showForm)}
            className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
          >
            <Settings className="w-4 h-4 mr-2" />
            {showForm ? 'Hide Options' : 'Customize'}
          </Button>
        </div>
        <p className="text-sm text-gray-400 mt-1">
          Generate AI predictions tailored to your preferences
        </p>
      </CardHeader>
      
      <CardContent>
        {showForm && options && (
          <div className="space-y-6 mb-6 pb-6 border-b border-white/10">
            {/* Total Odds Range */}
            <div>
              <Label className="text-white mb-2 block">Target Total Odds</Label>
              <Select 
                value={criteria.total_odds_range} 
                onValueChange={(value) => setCriteria({...criteria, total_odds_range: value})}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Select odds range" />
                </SelectTrigger>
                <SelectContent>
                  {options.total_odds_ranges.map(range => (
                    <SelectItem key={range.id} value={range.id}>{range.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Min Single Odds */}
            <div>
              <Label className="text-white mb-2 block">Minimum Odds per Selection</Label>
              <Select 
                value={criteria.min_odds} 
                onValueChange={(value) => setCriteria({...criteria, min_odds: value})}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Select minimum odds" />
                </SelectTrigger>
                <SelectContent>
                  {options.min_odds_options.map(opt => (
                    <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Markets */}
            <div>
              <Label className="text-white mb-2 block">Markets (select multiple)</Label>
              <div className="flex flex-wrap gap-2">
                {options.markets.map(market => (
                  <button
                    key={market.id}
                    type="button"
                    onClick={() => handleMarketToggle(market.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      criteria.markets.includes(market.id)
                        ? 'bg-purple-500 text-white'
                        : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
                    }`}
                  >
                    {market.name}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {criteria.markets.length === 0 ? 'All markets will be considered' : `${criteria.markets.length} market(s) selected`}
              </p>
            </div>

            {/* Leagues */}
            <div>
              <Label className="text-white mb-2 block">Leagues (select multiple)</Label>
              <div className="max-h-48 overflow-y-auto rounded-lg border border-white/10 p-2">
                <div className="flex flex-wrap gap-2">
                  {options.leagues.map(league => (
                    <button
                      key={league.id}
                      type="button"
                      onClick={() => handleLeagueToggle(league.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        criteria.leagues.includes(league.id)
                          ? 'bg-blue-500 text-white'
                          : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
                      }`}
                    >
                      {league.name}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {criteria.leagues.length === 0 ? 'All leagues will be considered' : `${criteria.leagues.length} league(s) selected`}
              </p>
            </div>
          </div>
        )}

        {/* Generate Button */}
        <Button
          onClick={generatePrediction}
          disabled={loading}
          className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white py-3"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generating Prediction...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Generate Personalized Prediction
            </>
          )}
        </Button>

        {/* Prediction Result */}
        {prediction && (
          <div className="mt-6 p-4 rounded-lg bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/20">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Target className="w-4 h-4 text-green-400" />
              Your Personalized Prediction
            </h4>
            
            <div className="space-y-3">
              {prediction.matches.map((match, index) => (
                <div key={index} className="p-3 rounded-lg bg-white/5 border border-white/10">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-white font-medium">{match.home_team} vs {match.away_team}</p>
                      <p className="text-gray-400 text-sm">
                        {match.country && match.league ? `${match.country} - ${match.league}` : match.league}
                      </p>
                      <p className="text-gray-500 text-xs flex items-center gap-1 mt-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(match.start_time)} at {formatMatchTime(match.start_time)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-purple-400 font-medium">{match.market}</p>
                      <p className="text-green-400 font-bold">@ {match.odds.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 pt-4 border-t border-white/10 flex justify-between items-center">
              <span className="text-gray-400">Total Combined Odds:</span>
              <span className="text-2xl font-bold text-green-400">{prediction.total_odds.toFixed(2)}</span>
            </div>
            
            {prediction.ai_reasoning && (
              <div className="mt-4 p-3 rounded-lg bg-white/5">
                <p className="text-sm text-gray-300">
                  <TrendingUp className="w-4 h-4 inline mr-2 text-blue-400" />
                  <strong>AI Analysis:</strong> {prediction.ai_reasoning}
                </p>
              </div>
            )}
            
            <p className="text-xs text-gray-500 mt-3 text-center">
              Generated at {formatDate(prediction.generated_at)} {formatMatchTime(prediction.generated_at)}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PersonalizedPredictions;
